import { Suspense, lazy, useEffect, useLayoutEffect, useRef } from 'react';
import { Box, MantineProvider } from '@mantine/core';
import { Route, Routes } from 'react-router-dom';
import betaTheme from './theme/beta-theme';
import { useBetaTheme } from './theme/use-beta-theme';
import './theme/beta.css';
import { BetaLayout } from './layout/beta-layout';
import { BetaHeader } from './layout/beta-header';
import { BetaFooter } from './layout/beta-footer';
import { BetaMobileTabs } from './layout/beta-mobile-tabs';

// Legal copy is shared with the classic site; plain Mantine picks up the beta theme.
const loadTerms = () => import('@/pages/terms');
const loadPrivacy = () => import('@/pages/privacy');
const loadHome = () => import('./pages/home/beta-home').then((m) => ({ default: m.BetaHome }));
const loadExpansions = () =>
  import('./pages/expansions/beta-expansions').then((m) => ({ default: m.BetaExpansions }));
const loadSearch = () => import('./pages/search/beta-search').then((m) => ({ default: m.BetaSearch }));
const loadCollections = () =>
  import('./pages/collections/beta-collections').then((m) => ({ default: m.BetaCollections }));
const loadLogin = () => import('./pages/login/beta-login').then((m) => ({ default: m.BetaLogin }));
const loadAccount = () => import('./pages/account/beta-account').then((m) => ({ default: m.BetaAccount }));
const loadForYou = () => import('./pages/for-you/beta-for-you').then((m) => ({ default: m.BetaForYou }));
const loadBulkEntry = () =>
  import('./pages/bulk-entry/beta-bulk-entry').then((m) => ({ default: m.BetaBulkEntry }));
const loadMcp = () => import('./pages/mcp/beta-mcp').then((m) => ({ default: m.BetaMcp }));
const loadBindings = () => import('./pages/bindings/beta-bindings').then((m) => ({ default: m.BetaBindings }));
const loadBinding = () => import('./pages/bindings/beta-binding').then((m) => ({ default: m.BetaBinding }));
const loadSetDetails = () =>
  import('./pages/set-details/beta-set-details').then((m) => ({ default: m.BetaSetDetails }));
const loadCardDetails = () =>
  import('./pages/card-details/beta-card-details').then((m) => ({ default: m.BetaCardDetails }));
const loadScanner = () => import('./pages/scanner/beta-scanner').then((m) => ({ default: m.BetaScanner }));
const loadWants = () => import('./pages/wants/beta-wants').then((m) => ({ default: m.BetaWants }));
const loadPublicBinding = () => import('./pages/bindings/beta-public-binding').then((m) => ({ default: m.BetaPublicBinding }));
const loadNotFound = () => import('./pages/beta-not-found').then((m) => ({ default: m.BetaNotFound }));

// Every page loader, so BetaApp can warm the chunks once the browser is idle.
const pageLoaders = [
  loadTerms, loadPrivacy, loadHome, loadExpansions, loadSearch, loadCollections, loadLogin,
  loadAccount, loadForYou, loadBulkEntry, loadMcp, loadBindings, loadBinding, loadSetDetails,
  loadCardDetails, loadScanner, loadWants, loadPublicBinding, loadNotFound,
];

const TermsPage = lazy(loadTerms);
const PrivacyPage = lazy(loadPrivacy);
const BetaHome = lazy(loadHome);
const BetaExpansions = lazy(loadExpansions);
const BetaSearch = lazy(loadSearch);
const BetaCollections = lazy(loadCollections);
const BetaLogin = lazy(loadLogin);
const BetaAccount = lazy(loadAccount);
const BetaForYou = lazy(loadForYou);
const BetaBulkEntry = lazy(loadBulkEntry);
const BetaMcp = lazy(loadMcp);
const BetaBindings = lazy(loadBindings);
const BetaBinding = lazy(loadBinding);
const BetaSetDetails = lazy(loadSetDetails);
const BetaCardDetails = lazy(loadCardDetails);
const BetaScanner = lazy(loadScanner);
const BetaWants = lazy(loadWants);
const BetaPublicBinding = lazy(loadPublicBinding);
const BetaNotFound = lazy(loadNotFound);

// First-paint shell while the lazy page chunk loads (e.g. hard refresh).
// Renders the real app chrome — header, footer, mobile tabs are already in
// the main chunk, so this costs no extra download — with a structured
// page-like skeleton for content. A bare skeleton box with no frame is what
// made the first ~300ms look broken.
function PageFallback() {
  return (
    <Box className="beta-frame">
      <BetaHeader />
      <Box component="main" className="beta-main">
        <div className="beta-container" role="status" aria-label="Loading page">
          <div className="beta-skeleton" style={{ height: 14, width: 180, marginBottom: 12 }} />
          <div className="beta-skeleton" style={{ height: 28, width: 'min(320px, 70%)', marginBottom: 8 }} />
          <div className="beta-skeleton" style={{ height: 14, width: 'min(440px, 90%)', marginBottom: 20 }} />
          <div className="beta-skeleton" style={{ height: 220, width: '100%' }} />
        </div>
      </Box>
      <BetaFooter />
      <BetaMobileTabs />
    </Box>
  );
}

export default function BetaApp() {
  const { theme } = useBetaTheme();
  // `.beta-root` is owned HERE (as the provider's parent) so it exists before
  // MantineProvider resolves its root — a descendant lookup during first render
  // returns null and Mantine falls back to the document color scheme, which is
  // what made Mantine components stuck on the wrong theme until a manual toggle.
  const rootRef = useRef<HTMLDivElement | null>(null);

  // Guarantee the scoped color-scheme attribute is correct from the first paint
  // (Mantine sets it in an effect that can be stale on initial load, leaving
  // components on the wrong theme until a manual toggle re-runs it). We own
  // `.beta-root`, so we can pin the attribute to the persisted beta theme.
  // Layout effect, not passive: this must apply before paint, not after.
  useLayoutEffect(() => {
    rootRef.current?.setAttribute('data-mantine-color-scheme', theme);
  }, [theme]);

  // Mantine portals (Menu, Select dropdown, Notifications) mount on
  // `document.body`, outside `.beta-root` where the focus-ring rule lives.
  // While the beta is mounted, flag the document so the same 2px ring covers
  // portal content too. Removed on unmount so production keeps its own rings.
  useEffect(() => {
    document.documentElement.setAttribute('data-beta-active', '1');
    document.documentElement.setAttribute('data-beta-scheme', theme);
    return () => {
      document.documentElement.removeAttribute('data-beta-active');
      document.documentElement.removeAttribute('data-beta-scheme');
    };
  }, [theme]);

  // Warm every page chunk once the browser is idle so in-app navigation
  // doesn't flash the Suspense fallback.
  useEffect(() => {
    const warm = () => pageLoaders.forEach((load) => load().catch(() => {}));
    if ('requestIdleCallback' in window) {
      const id = window.requestIdleCallback(warm);
      return () => window.cancelIdleCallback(id);
    }
    const id = setTimeout(warm, 2000);
    return () => clearTimeout(id);
  }, []);

  return (
    <div className="beta-root" ref={rootRef}>
      <MantineProvider
        theme={betaTheme}
        forceColorScheme={theme === 'dark' ? 'dark' : 'light'}
        getRootElement={() => rootRef.current ?? undefined}
        cssVariablesSelector=".beta-root"
      >
        <Suspense fallback={<PageFallback />}>
          <Routes>
            <Route element={<BetaLayout />}>
              <Route index element={<BetaHome />} />
              <Route path="expansions" element={<BetaExpansions />} />
              <Route path="search" element={<BetaSearch />} />
              <Route path="collections" element={<BetaCollections />} />
              <Route path="bindings" element={<BetaBindings />} />
              <Route path="bindings/:id" element={<BetaBinding />} />
              <Route path="b/:id" element={<BetaPublicBinding />} />
              <Route path="account" element={<BetaAccount />} />
              <Route path="for-you" element={<BetaForYou />} />
              <Route path="bulk-entry" element={<BetaBulkEntry />} />
              <Route path="scanner" element={<BetaScanner />} />
              <Route path="wants" element={<BetaWants />} />
              <Route path="mcp" element={<BetaMcp />} />
              <Route path="terms" element={<TermsPage />} />
              <Route path="privacy" element={<PrivacyPage />} />
              <Route
                path="expansions/:lang/:seriesKebab/:setKebab"
                element={<BetaSetDetails />}
              />
              <Route
                path="expansions/:lang/:seriesKebab/:setKebab/:cardNumber"
                element={<BetaCardDetails />}
              />
              <Route path="*" element={<BetaNotFound />} />
            </Route>
            {/* Login renders full-screen with no app chrome, matching the classic layout. */}
            <Route path="login" element={<BetaLogin />} />
            <Route path="login/*" element={<BetaLogin />} />
          </Routes>
        </Suspense>
      </MantineProvider>
    </div>
  );
}
